// The alert (search) endpoints — `@woo/api/extension/extension-email-alert.api.ng1.ts` and its
// client-contact twin (plan §7 phase 3, slice 2).
//
// ⚠️ GENERIC OVER THE ENTITY, which is the Phase-4 design constraint the plan asks for rather
// than an abstraction for its own sake. The two products differ only in the path segment:
// `extension-email-alert` serves candidate alerts and `client-contact-alert` serves contact
// ones, with the same method names, the same query parameters and the same container shape.
// Phase 4 is "much cheaper IF Phase 3 components were built generic over entity type" — so the
// split is made here, at the one place the two genuinely diverge, instead of being discovered
// later at a dozen call sites.
import { request } from './request.ts'
// The alert carries the sort the server persisted for it, in the same vocabulary the
// candidate query uses — imported rather than redeclared, for the reason `candidateFilters.ts`
// gives about `SocialHost`.
import type { AlertCandidateSortBy } from './candidates.ts'

/** Which product's alerts. The value is the path segment, which is the whole difference. */
export const AlertEntity = {
  Candidates: 'extension-email-alert',
  ClientContacts: 'client-contact-alert',
} as const
export type AlertEntity = (typeof AlertEntity)[keyof typeof AlertEntity]

/**
 * `IEntityAlertDTO` (entity-hunters-container-base.ng1.ts:92) plus the two fields of
 * `IExtensionEmailAlertDTO` that decide what the slim header renders.
 *
 * Deliberately partial: the full DTO carries eleven filter flags, a sort, ATS filters and an
 * automation mode, none of which anything ported reads yet. Declaring fields nothing consumes
 * would be inventing a contract.
 *
 * `extensionCriteria` IS declared now, because the edit form reads it — but only as an opaque
 * record. `IEntityExtensionCriteria` is `{}` upstream, filled per product, and the candidate
 * shape is described where it is consumed (`criteriaLoad.ts`'s `SavedCriteria`) rather than
 * here, so the client does not claim one product's tree is the DTO's.
 */
export interface ExtensionAlert {
  id: number
  name: string
  /**
   * ⚠️ EPOCH MILLIS, NOT AN ISO STRING. Upstream's DTO says `creationDate: Date`, which is
   * what the Java side serialises FROM — the JSON on the wire is a number
   * (`"creationDate":1785273747000`, measured). Reading it as a string type-checks, renders
   * nothing visibly wrong, and then throws the moment anything sorts by it:
   * `(a.creationDate ?? '').localeCompare(...)` is a TypeError on a number, which took the
   * whole panel down with "Bootstrap failed" when the alert list was re-sorted.
   */
  creationDate?: number
  /** A saved search imported from LinkedIn, Indeed, CV-Library, Monster or Reed. */
  hostSite?: string | null
  /** Set when the alert is bound to an ATS job, which then owns its criteria. */
  extensionJobId?: number | null
  linkedJobId?: number | null
  metadata?: { companyLogoUrl?: string | null } | null
  /**
   * The saved search criteria. ⚠️ EVERY KEY ARRIVES EXPLICITLY NULL when unset — the server
   * sends the whole tree, so `Object.keys` over it is all sixteen names regardless of what the
   * user filled in. See `pruneEmptyCriteria`.
   */
  extensionCriteria?: Record<string, unknown> | null
  /**
   * `HunterAutomationMode` — 'Enabled' | 'Paused', or absent.
   *
   * ⚠️ ABSENT IS A THIRD STATE, not a default. Automation has never been turned on for this
   * alert, which is what the "Activate automation" button is for; a boolean here would make
   * that indistinguishable from paused.
   */
  automationMode?: 'Enabled' | 'Paused' | null

  /**
   * How the recruiter last left the list, PERSISTED ON THE ALERT.
   *
   * ⚠️ THIS IS WHERE THE SORT AND FILTER LIVE, NOT IN `ExtensionStorageService`.
   * `changeSortOrder` and `changeShownCandidates` write both the query params AND these
   * fields (`extension-alert-candidates.component.ng1.ts:721-750`), and
   * `persistFilterAndSort` on the next fetch is what makes the server remember them. The
   * storage service holds `lastAlertViewed` and `shortlistLastSelectedState` — which alert
   * was open, not how it was sorted.
   *
   * ⚠️ AND THE SEVEN BOOLEANS ARE NOT OPTIONAL UPSTREAM (`extension-email-alert.api.ng1.ts:32`
   * declares them without `?`), so an alert always arrives with a full set. Optional here
   * only because a tenant that has never persisted one sends them absent, and `false` is the
   * right reading of absent for every one of them.
   */
  sortBy?: AlertCandidateSortBy
  filterLastRelevancyXDays?: number | null
  filterShowHiddenCandidates?: boolean
  filterShowFavoritesCandidatesOnly?: boolean
  filterHideViewedCandidates?: boolean
  filterShowExportedCandidatesOnly?: boolean
  filterShowMessagedCandidatesOnly?: boolean
  filterShowActionedAndUnactionedCandidates?: boolean

  /**
   * ⚠️ THE CONTACTS ALERT PERSISTS THE SAME SIX UNDER DIFFERENT NAMES
   * (`extension-client-contact-alert.api.ng1.ts:23`), and `sortBy` carries a different enum.
   * One container type serves both products — `getAlerts` differs only by a path segment —
   * so both sets live here rather than in a parallel interface nothing else would use.
   */
  filterShowHiddenContacts?: boolean
  filterShowFavoritesContactsOnly?: boolean
  filterHideViewedContacts?: boolean
  filterShowExportedContactsOnly?: boolean
  filterShowMessagedContactsOnly?: boolean
  filterShowActionedAndUnactionedContacts?: boolean
}

/** `IEntityAlertContainer` — the alert plus the extra info the list view needs. */
export interface ExtensionAlertContainer {
  id: number
  alert: ExtensionAlert
  /**
   * ⚠️ THE TITLE-CLASS METADATA LIVES HERE AND NOWHERE ELSE. The criteria store titles as bare
   * strings, so `titleClass`, `titlesAmount` and `topTitlesInClass` survive a reload only
   * through this block — `getTitlesTempCriteria` matches each saved title against it by
   * lower-cased text. Measured on the wire: `{"title":"developer","hasTitleClass":false,
   * "titleClass":true,"topTitlesInClass":[…],"titlesAmount":5,"isDev":true}`. This type used
   * to declare `{title, isDev}` alone, which is why the port rebuilt its tags without a class.
   */
  extraInfo?: {
    titlesExtraInfo?: Array<{
      title: string
      isDev?: boolean
      hasTitleClass?: boolean
      titleClass?: boolean
      titlesAmount?: number
      topTitlesInClass?: string[]
    }>
  }
}

/** `IEntityAlertNumbers` — the per-alert counts the tab strip and the list header show. */
export interface AlertNumbers {
  totalResults: number
  totalNewTopMatchesResults: number
  totalNewTopMatchesSinceLastViewedAlerts: number
}

/**
 * `getAlertsWithExtraDetailsByUserAndHost` — every alert this user can open on this host.
 *
 * ⚠️ `migrateAtsFilters: true` IS NOT OPTIONAL, and it is not a read flag. Upstream sends it on
 * every call (line 84), and the server uses the request to migrate any legacy ATS filter shape
 * on the alerts it returns. Dropping it because "we only want to read" would leave this client
 * the one caller that reads unmigrated alerts — a difference that shows up as filters silently
 * not applying, not as an error.
 *
 * `hostSite` is sent even when undefined upstream, where `$http` omits an undefined param; the
 * same happens here, so a call with no host asks for the ATS-session alerts exactly as it does
 * there.
 */
export async function getAlerts(entity: AlertEntity, hostSite?: string): Promise<ExtensionAlertContainer[]> {
  const params = new URLSearchParams({ migrateAtsFilters: 'true' })
  if (hostSite !== undefined && hostSite.length > 0) params.set('hostSite', hostSite)
  // A BARE PATH. `request` applies `apiUrl` itself (request.ts:71); wrapping it here too
  // doubles the base and only shows up in the extension, where the base is non-empty — the
  // SPA's is '' and would have hidden it indefinitely.
  return request<ExtensionAlertContainer[]>(
    'GET',
    `/chrome-extension-api/${entity}/getAlertsWithExtraDetailsByUserAndHost?${params.toString()}`,
  )
}

/**
 * `ShortlistAlertHelperService.NEW_ALERT_ID` — the sentinel an unsaved alert carries.
 *
 * A CLIENT-SIDE value: the server never sees it, because `saveAlert` converts it to an omitted
 * `id`. It lives beside the alert API rather than beside the candidate one because it is an
 * alert's identity, and both products' alerts use it.
 */
export const NEW_ALERT_ID = -1

/** `ShortlistAlertHelperService.NEW_ALERT_NAME` — blank, so the name field starts empty. */
export const NEW_ALERT_NAME = ''

/**
 * `ShortlistAlertCandidatesHelperService.getEmptyAlert()` — the alert "Add search" opens.
 *
 * ⚠️ IT IS A REAL OBJECT, NOT `undefined`, and that is the point. The edit screen resolves the
 * alert to edit by id; with no match it fell back to `containers[0]`, so "Add search" — had it
 * navigated at all — would have put the FIRST saved search's criteria on screen under a form
 * that saves as a new one. Quietly cloning somebody's search is a worse failure than the dead
 * button it replaced, which is why the two land together.
 *
 * The filter flags are all explicitly false rather than omitted, as upstream writes them: the
 * screen reads them to draw its toggles, and `undefined` would render an indeterminate control.
 */
export function emptyAlert(): ExtensionAlert {
  return {
    id: NEW_ALERT_ID,
    name: NEW_ALERT_NAME,
    creationDate: Date.now(),
    extensionCriteria: {},
    filterLastRelevancyXDays: null,
    filterShowHiddenCandidates: false,
    filterShowFavoritesCandidatesOnly: false,
    filterHideViewedCandidates: false,
    filterShowExportedCandidatesOnly: false,
    filterShowMessagedCandidatesOnly: false,
    filterShowActionedAndUnactionedCandidates: false,
  }
}

/**
 * `saveOrUpdateAlertWithExtraDetails` (extension-email-alert.api.ng1.ts:108).
 *
 * ⚠️ THE CRITERIA ARE THE BODY AND EVERYTHING ELSE IS A QUERY PARAM. The alert's name, id,
 * linked job and the criteria-changed flag all ride in the URL while `extensionCriteria` alone
 * is POSTed as JSON — so sending the whole DTO as the body, which is the shape the endpoint's
 * name suggests, saves an alert with no name and no id.
 *
 * ⚠️ AND `id` IS NULL FOR A NEW ALERT, NOT -1. The client carries `NEW_ALERT_ID = -1` as its own
 * sentinel and converts it at the boundary (`alert.id === -1 ? null : alert.id`); passing the
 * sentinel through creates a second alert on every save instead of updating.
 *
 * `isCriteriaChanged` is not a convenience flag: the server re-runs the search when it is true,
 * so sending `true` unconditionally re-queries on a pure rename.
 */
export interface SaveAlertParams {
  id: number
  name: string
  extensionCriteria: Readonly<Record<string, unknown>>
  isCriteriaChanged: boolean
  linkedJobId?: number | null
  sourceAlertId?: number | null
}

export async function saveAlert(entity: AlertEntity, params: SaveAlertParams): Promise<ExtensionAlertContainer> {
  const query = new URLSearchParams({
    name: params.name,
    isCriteriaChanged: String(params.isCriteriaChanged),
  })
  // Omitted rather than sent as the sentinel — see the note above.
  if (params.id !== NEW_ALERT_ID) query.set('id', String(params.id))
  if (params.sourceAlertId !== undefined && params.sourceAlertId !== null) {
    query.set('sourceAlertId', String(params.sourceAlertId))
  }
  if (params.linkedJobId !== undefined && params.linkedJobId !== null) {
    query.set('linkedJobId', String(params.linkedJobId))
  }

  return request<ExtensionAlertContainer>(
    'POST',
    `/chrome-extension-api/${entity}/saveOrUpdateAlertWithExtraDetails?${query.toString()}`,
    params.extensionCriteria,
  )
}

/** `ExtensionCriteriaFriendlyTextType` — the enum's ORDINALS are what the server sends. */
export const CriteriaFriendlyTextType = {
  FieldOfExpertise: 0,
  Subfields: 1,
  Skills: 2,
  WorkExperience: 3,
  Education: 4,
  Location: 5,
} as const
export type CriteriaFriendlyTextType =
  (typeof CriteriaFriendlyTextType)[keyof typeof CriteriaFriendlyTextType]

export interface CriteriaFriendlySection {
  title: string
  text: string[]
  type: CriteriaFriendlyTextType
}

export interface AlertCriteriaData {
  creationDate: number
  updateDate?: number | null
  criteria: CriteriaFriendlySection[]
}

/**
 * `getBeautifiedCriteria` — the alert's criteria as sentences, for the ☰ menu's
 * "Search criteria" panel.
 *
 * ⚠️ A POST WITH THE ID IN THE QUERY AND NO BODY AT ALL. Upstream sends `params: { alertId }`
 * and no `data`; putting the id in a JSON body answers 400, which is the fourth time this
 * client's verb-and-position pair has mattered more than its path (CLAUDE.md).
 */
export async function getBeautifiedCriteria(alertId: number): Promise<AlertCriteriaData> {
  return request(
    'POST',
    `/chrome-extension-api/alert-criteria/getBeautifiedCriteria?${new URLSearchParams({ alertId: String(alertId) }).toString()}`,
  )
}

/**
 * `deleteAlertById` — ⚠️ DESTROYS A RECRUITER'S SAVED SEARCH. Upstream never calls it without
 * `showDeleteDialogue` answering yes first (`entity-hunters-container-base.ng1.ts:605`), and
 * neither should anything here.
 */
export async function deleteAlertById(entity: AlertEntity, alertId: number): Promise<void> {
  return request(
    'DELETE',
    `/chrome-extension-api/${entity}/deleteAlertById?${new URLSearchParams({ alertId: String(alertId) }).toString()}`,
  )
}

/** `HunterAutomationMode` — the two states an alert with automation can be in. */
export const HunterAutomationMode = {
  Enabled: 'Enabled',
  Paused: 'Paused',
} as const
export type HunterAutomationMode = (typeof HunterAutomationMode)[keyof typeof HunterAutomationMode]

/**
 * `enableAutomation` (`extension-email-alert.api.ng1.ts:165`) — turn ATS automation ON for an
 * alert.
 *
 * ⚠️ THIS IS THE MOST OUTWARD-FACING CALL IN THE PRODUCT AND IT IS NOT UNDOABLE BY CALLING IT
 * AGAIN. The dialogue upstream puts in front of it says what it does: "all matching candidates
 * for <hunter> (<N>) will be messaged using the Woo Hunter automation", and "this automation
 * dynamically updates with every new candidate information found by Woo" — so it mails the
 * current set and keeps mailing new arrivals. On the baseline fixture alert that is 438 people.
 *
 * ⚠️ NEVER CALL IT WITHOUT THE CONFIRMATION. `ActivateAutomationDialogue` is not decoration
 * around this function, it is the other half of it: the button is one click, the dialogue is
 * what makes the click a decision. A caller that skips it has built a different feature.
 *
 * ⚠️ AND THE ID IS A QUERY PARAM WITH AN EMPTY JSON BODY. `params: { alertId }, data: {}` —
 * posting `{ alertId }` as the body answers 400, the fifth time this client's parameter
 * position has mattered more than its path.
 */
export async function enableAutomation(alertId: number): Promise<void> {
  await request(
    'POST',
    `/chrome-extension-api/extension-email-alert/enableAutomation?${new URLSearchParams({
      alertId: String(alertId),
    }).toString()}`,
    {},
  )
}

/**
 * `setAutomationMode` — pause or resume an alert whose automation is already on.
 *
 * Pausing is safe; RESUMING starts the messaging again, so the switch is as outward-facing as
 * the button above in one of its two directions. Upstream asks for no confirmation here,
 * because the alert is already automated and the switch says which way it is going.
 */
export async function setAutomationMode(alertId: number, mode: HunterAutomationMode): Promise<void> {
  await request(
    'POST',
    `/chrome-extension-api/extension-email-alert/setAutomationMode?${new URLSearchParams({
      alertId: String(alertId),
      mode,
    }).toString()}`,
    {},
  )
}
